import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Card,
  CardContent,
  Stack,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import { CompletedWorkItem } from '../../services/payrollService';

export interface EditableShiftData {
  actualStartTime: string;
  actualEndTime: string;
  hourlyRate: number;
  notes: string;
  totalHours: number;
}

interface ShiftEditApprovalDialogProps {
  open: boolean;
  onClose: () => void;
  onSave?: (workId: number, updatedData: EditableShiftData) => Promise<void>;
  workItem: CompletedWorkItem | null;
  mode: 'approve' | 'edit';
  loading?: boolean;
}

export const ShiftEditApprovalDialog: React.FC<ShiftEditApprovalDialogProps> = ({
  open,
  onClose,
  onSave,
  workItem,
  mode = 'approve',
  loading = false
}) => {
  const [editData, setEditData] = useState<EditableShiftData>({
    actualStartTime: '',
    actualEndTime: '',
    hourlyRate: 0,
    notes: '',
    totalHours: 0
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const isEdit = mode === 'edit';

  // Initialize form data when workItem changes
  useEffect(() => {
    if (workItem && open) {
      setEditData({
        actualStartTime: workItem.actualStart || '',
        actualEndTime: workItem.actualEnd || '',
        hourlyRate: workItem.hourlyRate,
        notes: '',
        totalHours: workItem.totalHours
      });
      setError('');
    }
  }, [workItem, open]);

  // Calculate total hours when start/end times change
  useEffect(() => {
    if (editData.actualStartTime && editData.actualEndTime) {
      const start = new Date(`1970-01-01T${editData.actualStartTime}`);
      const end = new Date(`1970-01-01T${editData.actualEndTime}`);
      const diffMs = end.getTime() - start.getTime();
      const totalHours = diffMs / (1000 * 60 * 60); // Convert to hours
      if (totalHours > 0) {
        setEditData(prev => ({ ...prev, totalHours: Math.round(totalHours * 100) / 100 }));
      }
    }
  }, [editData.actualStartTime, editData.actualEndTime]);

  const handleSave = async () => {
    if (!workItem || !onSave) return;
    
    setError('');
    
    // Validation
    if (isEdit) {
      if (!editData.actualStartTime || !editData.actualEndTime) {
        setError('Start and end times are required');
        return;
      }
      if (editData.actualEndTime <= editData.actualStartTime) {
        setError('End time must be after start time');
        return;
      }
      // Note: hourly rate is intentionally NOT validated here — it is sourced
      // from Billing Rates → Staff Rates and re-applied per-segment whenever
      // payroll is generated. Editing this shift only adjusts the
      // start/end/duration; the rate stays driven by configuration.
    }

    try {
      setSaving(true);
      await onSave(workItem.id, editData);
      onClose();
    } catch (error) {
      console.error('Error saving shift:', error);
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!open || !workItem) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEdit ? 'Edit Approved Shift' : 'Approve Shift'}
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Original Shift Information */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Shift Information
            </Typography>
            <Stack spacing={2}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  <strong>Staff:</strong> {workItem.staffName} ({workItem.employeeId})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Client:</strong> {workItem.clientName}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  <strong>Date:</strong> {workItem.shiftDate}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Scheduled:</strong> {workItem.scheduledStart} - {workItem.scheduledEnd}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  <strong>Actual:</strong> {workItem.actualStart} - {workItem.actualEnd}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Total Hours:</strong> {workItem.totalHours}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  <strong>Base Rate (Billing Rates):</strong>{' '}
                  {workItem.hourlyRate > 0 ? `$${workItem.hourlyRate.toFixed(2)}/hr` : '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Total Pay (segmented):</strong> ${workItem.totalPay.toFixed(2)}
                </Typography>
              </Box>
              {workItem.rateSource && (
                <Typography variant="caption" color="text.secondary">
                  {workItem.rateSource}
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>

        {isEdit && (
          <>
            <Divider sx={{ mb: 3 }} />
            
            {/* Editable Fields */}
            <Typography variant="h6" gutterBottom>
              Edit Shift Details
            </Typography>
            
            <Stack spacing={3}>
              <Box display="flex" gap={2}>
                <TextField
                  fullWidth
                  label="Actual Start Time"
                  type="time"
                  value={editData.actualStartTime}
                  onChange={(e) => setEditData(prev => ({ ...prev, actualStartTime: e.target.value }))}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <TextField
                  fullWidth
                  label="Actual End Time"
                  type="time"
                  value={editData.actualEndTime}
                  onChange={(e) => setEditData(prev => ({ ...prev, actualEndTime: e.target.value }))}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
              
              <Alert severity="info">
                The hourly rate cannot be edited per-shift. It is sourced from{' '}
                <strong>Billing Rates → Staff Rates</strong> and re-applied per-segment whenever
                payroll is generated. Adjust start/end times here; pay will be recalculated
                automatically using the matching StaffPay segments.
              </Alert>

              <TextField
                fullWidth
                label="Total Hours"
                type="number"
                value={editData.totalHours}
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    backgroundColor: 'action.hover'
                  }
                }}
                helperText="Calculated automatically from start/end times"
              />
              
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Edit Notes"
                value={editData.notes}
                onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Add any notes about the changes made to this shift..."
              />
            </Stack>
            
            {/* Summary */}
            <Card sx={{ mt: 3, bgcolor: 'background.default' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Updated Summary
                </Typography>
                <Stack spacing={1}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">
                      <strong>Total Hours:</strong> {editData.totalHours}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Base Rate:</strong>{' '}
                      {workItem.hourlyRate > 0 ? `$${workItem.hourlyRate.toFixed(2)}/hr` : '—'}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Final pay will be re-priced from Billing Rates StaffPay segments after save.
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </>
        )}

        {!isEdit && (
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Approval Notes (optional)"
            value={editData.notes}
            onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
            sx={{ mt: 2 }}
            placeholder="Add any notes about this approval..."
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={saving || loading}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? 'Saving...' : (isEdit ? 'Update Shift' : 'Approve')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
